import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import os from '@incentivegames/ig-types/lib/constants/os';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { numberUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/numberUtilities';
import { TokenState, UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import routes from '../../../constants/routes';
import { useSelector, useDispatch } from 'react-redux';
import { IRootState } from 'store/serverInterfaces';
import { clearError, getEntries } from 'store/actions/dreamteamActions';
import { IGame, IGameEntry } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { IInitialState, IViewDataProps } from 'interfaces/gameInterfaces';
import { HomeFunctions } from './homeFunctions';
import HomeView from './HomeView';
import useScreenHeight from 'hooks/useScreenHeight';
import LandingView from './LandingView/index';
import Loading from '../../Loading';

export const shouldShowHomeView = (displayStart: number, displayEnd: number, openTime: number) => {
  if (displayStart === 0 && displayEnd === 0 && openTime === 0) return true;
  return Date.now() >= displayStart && Date.now() < displayEnd && Date.now() >= openTime;
};

const Home = () => {
  const history = useHistory();
  const userContext = useContext(UserContext);
  const dreamteamReducer: IInitialState = useSelector((state: IRootState) => state.DreamteamReducer);
  const dispatch = useDispatch();

  const currentGame: IGame | undefined = dreamteamReducer.games?.find((game: IGame) => game.gameId === dreamteamReducer.currentGameId);
  const uniqueId = numberUtilities.getUniqueId();
  const [loading, setLoading] = useState<boolean>(true);
  const displayStart = currentGame?.displayStartTime || 0;
  const displayEnd = currentGame?.displayEndTime || 0;
  const openTime = currentGame?.openTime || 0;

  useScreenHeight();

  useEffect(() => {
    const timeout = setTimeout(() => {
      // if 5s have passed and we haven't received a response from the client then assume we aren't getting one
      if (userContext.user.tokenState === TokenState.NoTokenReceived) {
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userContext.user.token && dreamteamReducer.currentGameId) {
      dispatch(getEntries(userContext.user.token, dreamteamReducer.currentGameId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, userContext.user, dreamteamReducer.currentGameId]);

  useEffect(() => {
    if (dreamteamReducer.error) {
      const errorTimeout = setTimeout(() => {
        dispatch(clearError());
        userContext.setPlayerData('');
      }, 20000);
      return () => clearTimeout(errorTimeout);
    }
  }, [dreamteamReducer.error, dispatch, userContext]);

  useEffect(() => {
    if (dreamteamReducer.entries && dreamteamReducer.games) {
      const hasUserEntryCurrentGame = !!dreamteamReducer.entries.filter((entry: IGameEntry) => entry.gameId === dreamteamReducer.currentGameId).length;
      const gameAvailable = !!dreamteamReducer.games.filter((game: IGame) => game.nextGame).length;

      if (hasUserEntryCurrentGame || !gameAvailable) {
        history.push({
          pathname: routes.FIVES.route,
          search: window.location.search
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamteamReducer.entries]);

  useEffect(() => {
    const validTokenAndEntriesRetrieved = userContext.user.tokenState === TokenState.ValidToken && !!dreamteamReducer.entries;
    const invalidToken = userContext.user.tokenState === TokenState.InvalidToken;
    setLoading(!(validTokenAndEntriesRetrieved || invalidToken || dreamteamReducer.error));
  }, [userContext.user, dreamteamReducer.entries, dreamteamReducer.error]);

  const viewDataProps: IViewDataProps = {
    handleStartPlayingClick: () => HomeFunctions.handleStartPlayingClick(dispatch, uniqueId, history, currentGame, userContext.user.token, userContext.user.promotion),
    loading,
    tokenState: userContext.user.tokenState,
    error: dreamteamReducer.error,
    extraMargin: communicationUtilities.getOs() === os.ANDROID ? '100px' : '0',
    formatDateTime: HomeFunctions.formatDateTime,
    handleHowToPlayClick: HomeFunctions.howToPlayClick,
    roundStartTime: !!currentGame?.revealSchedule ? Math.min(...currentGame.revealSchedule.map((reveal) => reveal.revealTime)) : undefined,
    competitionName: currentGame?.competitionName
  };

  if (loading || !currentGame) {
    return <Loading showText={false} />;
  }

  return shouldShowHomeView(displayStart, displayEnd, openTime) ? <HomeView data={viewDataProps} /> : <LandingView data={viewDataProps} />;
};

export default Home;
